import type React from 'react'
import { useEffect, useState } from 'react'

import { useCrawler } from '@/hooks'
import { NestClient } from '@/nest'
import type {
  CrawlerExtra,
  CrawlerIssueItem,
  ImportSitemapResultUrlItem,
} from '@/types'

type Props = {
  uuid?: string
}

type State = {
  error?: string
  overview?: unknown
  pages?: unknown
}

type CrawlResult = {
  extra?: CrawlerExtra
  issues: CrawlerIssueItem[]
  result: ImportSitemapResultUrlItem[]
}

type RunnerProps = {
  onDone: (crawl: CrawlResult) => void
  url: string
}

const styles = {
  panel: {
    borderBottom: '1px solid #ddd',
    display: 'flex',
    gap: 8,
    padding: 16,
  },
  pre: {
    fontFamily: 'monospace',
    fontSize: 12,
    padding: 24,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-all' as const,
  },
}

// Mounted per crawl and keyed by the address, so every run starts on a fresh
// hook instead of reading the last run's finished state.
const CrawlRunner: React.FC<RunnerProps> = ({ onDone, url }) => {
  // The generator page crawls anonymously with meta on, so this asks for the
  // same thing: titles and h1s, and the 300-page free cap.
  const endpoint = new URL(import.meta.env.VITE_GENERATOR_URL!)

  endpoint.searchParams.set('url', url)
  endpoint.searchParams.set('meta', 'true')

  const { done, error, extra, issues, progress, result } = useCrawler({
    url: endpoint.toString(),
  })

  useEffect(() => {
    if (done) {
      onDone({ extra, issues, result })
    }
  }, [done])

  return (
    <span>
      {error || `crawling: ${progress.scanned} of ${progress.totalCount}`}
    </span>
  )
}

// TEMPORARY (OCT-4636): the report screen still renders its sample data for
// every uuid, so a report created from a real crawl cannot be checked on it.
// `?raw=1` shows what the API actually returned for this report, and can send
// a second crawl into it — the only way to see a delta before the screen is
// wired. Delete together with the screen's mock.
export const RawReport: React.FC<Props> = ({ uuid }) => {
  const [state, setState] = useState<State>({})
  const [url, setUrl] = useState('')
  const [crawling, setCrawling] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const load = () => {
    if (!uuid) {
      return
    }

    Promise.all([
      NestClient.sitemapReport.overview(uuid),
      NestClient.sitemapReport.pages(uuid),
    ])
      .then(([overview, pages]) => {
        setState({ overview, pages })
        // The point of a second crawl is the same site again, so the address the
        // report already has is the default.
        setUrl((current) => current || overview.site.url)
      })
      .catch((error) => {
        setState({ error: String(error?.message ?? error) })
      })
  }

  useEffect(load, [uuid])

  const ingest = (crawl: CrawlResult) => {
    if (!uuid || saving) {
      return
    }

    setSaving(true)

    NestClient.sitemapReport
      .crawl(uuid, {
        faviconUrl: crawl.extra?.icon ?? null,
        pages: [...crawl.result, ...crawl.issues],
        title: crawl.extra?.title ?? null,
        url: crawling ?? url,
      })
      .then(() => {
        setCrawling(null)
        setSaving(false)
        load()
      })
      .catch((error) => {
        setCrawling(null)
        setSaving(false)
        setState({ error: String(error?.message ?? error) })
      })
  }

  return (
    <>
      <div style={styles.panel}>
        <input
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://website.com"
          value={url}
        />
        <button
          disabled={!url || Boolean(crawling)}
          onClick={() => setCrawling(url)}
          type="button"
        >
          Crawl again into this report
        </button>
        {crawling && !saving && (
          <CrawlRunner key={crawling} onDone={ingest} url={crawling} />
        )}
        {saving && <span>saving the crawl…</span>}
      </div>

      <pre style={styles.pre}>{JSON.stringify(state, null, 2)}</pre>
    </>
  )
}
